// src/pages/admin/products/Recipes.jsx
import { useState, useEffect } from 'react'
import { 
  Plus, 
  Eye, 
  Edit, 
  Trash2, 
  Search, 
  RefreshCw, 
  Filter,
  ChefHat,
  DollarSign,
  Package,
  XCircle,
  ChevronDown
} from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import RecipesService from '../../../services/recipesService'
import ProductService from '../../../services/productService'
import RawMaterialsService from '../../../services/rawMaterialsService'
import CreateRecipeModal from '../../../components/modals/CreateRecipeModal'
import EditRecipeModal from '../../../components/modals/EditRecipeModal'
import ViewRecipeModal from '../../../components/modals/ViewRecipeModal'
import DeleteConfirmModal from '../../../components/modals/DeleteConfirmModal'
import CardLoader from '../../../components/ui/CardLoader'

// Toast Component
const Toast = ({ message, type, onClose }) => (
  <div 
    className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg flex items-center space-x-2 ${
      type === 'success' ? 'bg-green-500 text-white' : 
      type === 'error' ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'
    }`}
    style={{ zIndex: 99999 }}
  >
    {type === 'success' ? <Package className="h-5 w-5" /> : 
     type === 'error' ? <XCircle className="h-5 w-5" /> : <ChefHat className="h-5 w-5" />}
    <span>{message}</span>
    <button onClick={onClose} className="ml-2">
      <XCircle className="h-4 w-4" />
    </button>
  </div>
)

const Recipes = () => {
  // State management
  const [recipes, setRecipes] = useState([])
  const [products, setProducts] = useState([])
  const [rawMaterials, setRawMaterials] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [toast, setToast] = useState(null)
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedRecipe, setSelectedRecipe] = useState(null)
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('')
  const [productFilter, setProductFilter] = useState('all')
  const [sortBy, setSortBy] = useState('name')
  const [sortOrder, setSortOrder] = useState('asc')

  // Helper function to safely get nested values
  const safeGet = (obj, path, defaultValue = null) => {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue
      }, obj)
    } catch {
      return defaultValue
    }
  }

  // Helper function to validate recipe object
  const validateRecipe = (recipe) => {
    if (!recipe || typeof recipe !== 'object') return false
    return recipe._id && recipe.name
  }

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('')
    setProductFilter('all')
    setSortBy('name')
    setSortOrder('asc')
  }

  // Fetch recipes using service
  const fetchRecipes = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await RecipesService.getAllRecipes({ page: 1, limit: 1000 })
      
      if (response && response.success) {
        const recipesData = safeGet(response, 'data.recipes', []) || 
                          safeGet(response, 'data.docs', []) || 
                          safeGet(response, 'data', [])
        
        console.log('🍴 Recipes response structure:', response.data)
        console.log('🍴 Extracted recipes data:', recipesData)
        
        // Log sample recipe structure
        if (recipesData.length > 0) {
          console.log('🍴 Sample recipe structure:', recipesData[0])
        }
        
        // Filter out invalid recipes
        const validRecipes = Array.isArray(recipesData) 
          ? recipesData.filter(validateRecipe)
          : []
        
        console.log('🍴 Valid recipes loaded:', validRecipes.length)
        setRecipes(validRecipes)
      } else {
        console.warn('⚠️ Unexpected response format:', response)
        setError('Failed to load recipes')
        setRecipes([])
      }
    } catch (error) {
      console.error('❌ Error fetching recipes:', error)
      setError(error.message || 'Failed to load recipes')
      setRecipes([])
    } finally {
      setLoading(false)
    }
  }

  // Fetch products for dropdown (only recipe-based products)
  const fetchProducts = async () => {
    try {
      console.log('🛍️ Fetching recipe-based products for dropdown...')
      const response = await ProductService.getAllProducts()
      
      if (response && response.success) {
        const productsData = response.data?.products || []
        console.log('🛍️ All products data extracted:', productsData)
        
        // Filter only recipe-based products
        const recipeBased = Array.isArray(productsData) 
          ? productsData.filter(product => product.type === 'recipe_based')
          : []
        
        console.log('🍳 Recipe-based products filtered:', recipeBased)
        setProducts(recipeBased)
      } else {
        console.warn('⚠️ Failed to fetch products:', response)
        setProducts([])
      }
    } catch (error) {
      console.error('❌ Error fetching products:', error)
      setProducts([])
    }
  }

  // Fetch raw materials for dropdown
  const fetchRawMaterials = async () => {
    try {
      console.log('🧱 Fetching raw materials for dropdown...')
      const response = await RawMaterialsService.getAllRawMaterials()
      
      if (response && response.success) {
        const rawMaterialsData = response.data?.rawMaterials || []
        console.log('🧱 Raw materials data extracted:', rawMaterialsData)
        setRawMaterials(Array.isArray(rawMaterialsData) ? rawMaterialsData : [])
      } else {
        console.warn('⚠️ Failed to fetch raw materials:', response)
        setRawMaterials([])
      }
    } catch (error) {
      console.error('❌ Error fetching raw materials:', error)
      setRawMaterials([])
    }
  }

  // Filter and search recipes
  const getFilteredRecipes = () => {
    let filtered = [...recipes]

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(recipe => 
        recipe.name?.toLowerCase().includes(searchLower) ||
        recipe.product?.name?.toLowerCase().includes(searchLower) ||
        recipe._id?.toLowerCase().includes(searchLower)
      )
    }

    // Product filter
    if (productFilter !== 'all') {
      filtered = filtered.filter(recipe => {
        const productId = safeGet(recipe, 'product._id', recipe.product)
        return productId === productFilter
      })
    }

    // Sort
    filtered.sort((a, b) => {
      let valueA, valueB

      switch (sortBy) {
        case 'name':
          valueA = a.name?.toLowerCase() || ''
          valueB = b.name?.toLowerCase() || ''
          break
        case 'product':
          valueA = a.product?.name?.toLowerCase() || ''
          valueB = b.product?.name?.toLowerCase() || ''
          break
        case 'cost':
          valueA = Number(a.cost) || 0
          valueB = Number(b.cost) || 0
          break
        case 'updatedAt':
          valueA = new Date(a.updatedAt || 0)
          valueB = new Date(b.updatedAt || 0)
          break
        default:
          valueA = a.name?.toLowerCase() || ''
          valueB = b.name?.toLowerCase() || ''
      }

      if (sortOrder === 'asc') {
        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0
      } else {
        return valueA > valueB ? -1 : valueA < valueB ? 1 : 0
      }
    })

    return filtered
  }

  // Get recipe statistics
  const getRecipeStats = () => {
    const stats = {
      total: recipes.length,
      averageCost: 0,
      totalValue: 0,
      productsCovered: new Set()
    }

    recipes.forEach(recipe => {
      const cost = Number(recipe.cost) || 0
      stats.totalValue += cost
      if (recipe.product?._id) {
        stats.productsCovered.add(recipe.product._id)
      }
    })

    stats.averageCost = stats.total > 0 ? stats.totalValue / stats.total : 0
    stats.uniqueProducts = stats.productsCovered.size

    return stats
  }

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      console.log('🚀 Starting recipes data load...')
      await fetchProducts()
      await fetchRawMaterials()
      await fetchRecipes()
      console.log('✅ Recipes data load complete')
    }
    
    loadData()
  }, [])

  // Handle create recipe
  const handleCreateRecipe = async (recipeData) => {
    try {
      const response = await RecipesService.createRecipe(recipeData)
      
      if (response && response.success) {
        showToast('Recipe created successfully', 'success')
        setShowCreateModal(false)
        fetchRecipes() // Refresh the list
      } else {
        throw new Error(response?.message || 'Failed to create recipe')
      }
    } catch (error) {
      console.error('❌ Error creating recipe:', error)
      showToast(error.message || 'Failed to create recipe', 'error')
    }
  }

  // Handle edit recipe
  const handleEditRecipe = async (recipeData) => {
    try {
      if (!selectedRecipe?._id) {
        throw new Error('No recipe selected for editing')
      }
      
      const response = await RecipesService.updateRecipe(selectedRecipe._id, recipeData)
      
      if (response && response.success) {
        showToast('Recipe updated successfully', 'success')
        setShowEditModal(false)
        setSelectedRecipe(null)
        fetchRecipes() // Refresh the list
      } else {
        throw new Error(response?.message || 'Failed to update recipe')
      }
    } catch (error) {
      console.error('❌ Error updating recipe:', error)
      showToast(error.message || 'Failed to update recipe', 'error')
    }
  }

  // Handle delete recipe
  const handleDeleteRecipe = async () => {
    try {
      if (!selectedRecipe?._id) {
        throw new Error('No recipe selected for deletion')
      }
      
      const response = await RecipesService.deleteRecipe(selectedRecipe._id)
      
      if (response && response.success) {
        showToast('Recipe deleted successfully', 'success')
        setShowDeleteModal(false)
        setSelectedRecipe(null)
        fetchRecipes() // Refresh the list
      } else {
        throw new Error(response?.message || 'Failed to delete recipe')
      }
    } catch (error) {
      console.error('❌ Error deleting recipe:', error)
      showToast(error.message || 'Failed to delete recipe', 'error')
    }
  }

  // Handle view recipe
  const handleViewRecipe = (recipe) => {
    setSelectedRecipe(recipe)
    setShowViewModal(true)
  }

  // Handle edit recipe click
  const handleEditRecipeClick = (recipe) => {
    setSelectedRecipe(recipe)
    setShowEditModal(true)
  }

  // Handle delete recipe click
  const handleDeleteRecipeClick = (recipe) => {
    setSelectedRecipe(recipe)
    setShowDeleteModal(true)
  }

  // Format cost for display
  const formatCost = (cost) => {
    if (typeof cost === 'number') {
      return cost.toFixed(2)
    }
    return '0.00'
  }

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleDateString()
    } catch {
      return 'N/A'
    }
  }

  const filteredRecipes = getFilteredRecipes()
  const stats = getRecipeStats()

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Recipes</h1>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={fetchRecipes}
            disabled={loading}
            className="flex items-center"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Recipe
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <ChefHat className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Recipes</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <Package className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Products Covered</p>
                <p className="text-2xl font-bold text-gray-900">{stats.uniqueProducts}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <DollarSign className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg Cost</p>
                <p className="text-2xl font-bold text-gray-900">Rs. {formatCost(stats.averageCost)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <DollarSign className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Value</p>
                <p className="text-2xl font-bold text-gray-900">Rs. {formatCost(stats.totalValue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card className="mb-6">
        <CardContent className="p-6">
          {/* Filter Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Filter Recipes</h2>
            </div>
            {(searchTerm || productFilter !== 'all' || sortBy !== 'name') && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="text-orange-500 border-orange-500 hover:bg-orange-50"
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Filter Controls */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <Input
                type="text"
                placeholder="Search recipes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 border-gray-300"
              />
            </div>

            {/* Product Filter */}
            <div className="relative">
              <select
                value={productFilter}
                onChange={(e) => setProductFilter(e.target.value)}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
              >
                <option value="all">All Products</option>
                {products.map(product => (
                  <option key={product._id} value={product._id}>
                    {product.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Sort */}
            <div className="relative">
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-')
                  setSortBy(field)
                  setSortOrder(order)
                }}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
              >
                <option value="name-asc">Name (A-Z)</option>
                <option value="name-desc">Name (Z-A)</option>
                <option value="product-asc">Product (A-Z)</option>
                <option value="product-desc">Product (Z-A)</option>
                <option value="cost-asc">Cost (Low-High)</option>
                <option value="cost-desc">Cost (High-Low)</option>
                <option value="updatedAt-desc">Recently Updated</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Results Count */}
          <div className="text-sm text-gray-600">
            Showing {filteredRecipes.length} of {recipes.length} recipes
          </div>
        </CardContent>
      </Card>

      {/* Recipes Table */}
      {loading ? (
        <CardLoader title="Loading recipes..." />
      ) : error ? (
        <Card className="bg-white">
          <CardContent className="py-12">
            <div className="text-center">
              <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button onClick={fetchRecipes} leftIcon={RefreshCw}>
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white">
          <CardContent className="p-0">
            {filteredRecipes.length === 0 ? (
              <div className="text-center py-12">
                <ChefHat className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {searchTerm || productFilter !== 'all' ? 'No recipes found' : 'No recipes available'}
                </h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm || productFilter !== 'all' 
                    ? 'Try adjusting your search criteria.' 
                    : 'Create your first recipe to get started.'
                  }
                </p>
                {!searchTerm && productFilter === 'all' && (
                  <Button
                    onClick={() => setShowCreateModal(true)}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Recipe
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        RECIPE ID
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        RECIPE NAME
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        PRODUCT
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        COST (RS)
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        LAST UPDATE
                      </th>
                      <th scope="col" className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ACTIONS
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredRecipes.map((recipe, index) => (
                      <tr key={recipe._id || index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {recipe._id ? recipe._id.slice(-6).toUpperCase() : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {recipe.name || 'Unnamed Recipe'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {recipe.product?.name || 'Unknown Product'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            Rs. {formatCost(recipe.cost)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {formatDate(recipe.updatedAt)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleViewRecipe(recipe)}
                              className="text-gray-400 hover:text-gray-600 p-1 rounded"
                              title="View Recipe"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleEditRecipeClick(recipe)}
                              className="text-gray-400 hover:text-gray-600 p-1 rounded"
                              title="Edit Recipe"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteRecipeClick(recipe)}
                              className="text-gray-400 hover:text-red-600 p-1 rounded"
                              title="Delete Recipe"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateRecipeModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateRecipe}
          products={products}
          rawMaterials={rawMaterials}
        />
      )}

      {showEditModal && selectedRecipe && (
        <EditRecipeModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false)
            setSelectedRecipe(null)
          }}
          onSubmit={handleEditRecipe}
          recipe={selectedRecipe}
          products={products}
          rawMaterials={rawMaterials}
        />
      )}

      {showViewModal && selectedRecipe && (
        <ViewRecipeModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false)
            setSelectedRecipe(null)
          }}
          recipe={selectedRecipe}
        />
      )}

      {showDeleteModal && selectedRecipe && (
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false)
            setSelectedRecipe(null)
          }}
          onConfirm={handleDeleteRecipe}
          title="Delete Recipe"
          message={`Are you sure you want to delete "${selectedRecipe.name}"? This action cannot be undone.`}
        />
      )}
    </div>
  )
}

export default Recipes